import type { ComponentType, SVGProps } from "react";
import { SiClaude } from "@icons-pack/react-simple-icons";
import type { LucideIcon } from "lucide-react";
import { Braces, Mail, Radio, Repeat } from "lucide-react";
import { roadmapItem } from "@/lib/roadmap";
import { routePath, type RouteName } from "@/lib/routes";

/**
 * The /integrations catalog — single source of truth for the directory rows
 * and the hero's derived counts. Every entry carries the same shape
 * (name · category · mark · flow · status · depth · detail), per
 * docs/page-briefs/integrations.md §4.2 and the "Live ledger" handoff.
 *
 * Statuses are HONEST (claims audit 2026-09-27): only what the product
 * actually ships today is `live`; a `roadmap` entry exists only when
 * lib/roadmap.ts lists it, and carries that file's date. Connectors with no
 * code and no roadmap entry (GA4, Meta Pixel, Zapier, Make, Slack,
 * WordPress, Shopify, SDKs, a customer CLI) are not listed at all. The hero
 * counts are derived from this array — never hardcode them in prose
 * (brief §8.5).
 */

export type CategoryId = "automation" | "communication" | "developer" | "ai";

export interface Category {
  id: CategoryId;
  label: string;
  /** Category accent — a `--color-data-*` token (AI borrows the brand). */
  color: string;
}

export const CATEGORIES: Category[] = [
  { id: "developer", label: "Developer", color: "var(--color-data-cyan)" },
  { id: "ai", label: "AI", color: "var(--color-brand-600)" },
  { id: "communication", label: "Communication", color: "var(--color-data-green)" },
  { id: "automation", label: "Automation", color: "var(--color-data-violet)" },
];

export type Status = "live" | "roadmap";
export type Depth = "native" | "via API";

/** A row's 44px tile art. Two kinds, deliberately distinguishable:
 *  - `brand`: the vendor's own mark, bundled locally via
 *    @icons-pack/react-simple-icons (NO CDN — next.config's remotePatterns
 *    stays empty). Sits on a neutral surface tile so the mark keeps its own
 *    color story.
 *  - `glyph`: a lucide stroke icon for the things that have no vendor mark
 *    (our own primitives) — brand-tinted tile, brand-colored stroke. */
export type Mark =
  | { kind: "brand"; Icon: ComponentType<SVGProps<SVGSVGElement> & { size?: number }> }
  | { kind: "glyph"; Icon: LucideIcon; tint?: string };

export interface Integration {
  id: string;
  name: string;
  category: CategoryId;
  mark: Mark;
  /** One-line in/out statement — what literally crosses the wire. */
  flow: string;
  status: Status;
  /** Roadmap entries carry their lib/roadmap.ts date here ("Q1 2027"). */
  statusDate?: string;
  depth: Depth;
  /** One-sentence expansion shown under the name. */
  detail: string;
  /** Where the entry's depth is owned (topic-ownership matrix). */
  href?: RouteName;
}

export const CATALOG: Integration[] = [
  {
    id: "rest",
    name: "REST API",
    category: "developer",
    mark: { kind: "glyph", Icon: Braces },
    flow: "links CRUD · domains · per-link stats",
    status: "live",
    depth: "native",
    detail: "Links, domains and stats for what you create over the API — MCP covers the whole workspace.",
    href: "developers",
  },
  {
    id: "stream",
    name: "Click stream (SSE)",
    category: "developer",
    mark: { kind: "glyph", Icon: Radio },
    flow: "click events ⟶ your client, live (you pull)",
    status: "live",
    depth: "native",
    detail: "Every human click on your API-created links, the moment it happens.",
    href: "developers",
  },
  {
    id: "mcp",
    name: "Claude / MCP",
    category: "ai",
    mark: { kind: "brand", Icon: SiClaude },
    flow: "LLM tool-calls ⟶ link creation + analytics",
    // Shipped Aug 2026 (promised Sept 2026 — see lib/roadmap.ts + /changelog).
    status: "live",
    depth: "native",
    detail: "Let your LLM manage links as a tool call.",
    href: "aiMcp",
  },
  {
    id: "email",
    name: "Email forwarding",
    category: "communication",
    mark: { kind: "glyph", Icon: Mail },
    flow: "anything@your-domain ⟶ your real inbox",
    status: "live",
    depth: "native",
    detail: "Catch-all forwarding on your link domain.",
    href: "customDomains",
  },
  {
    id: "webhooks",
    name: "Webhooks",
    category: "automation",
    mark: { kind: "glyph", Icon: Repeat },
    flow: "link events ⟶ your endpoint (planned)",
    status: "roadmap",
    statusDate: roadmapItem("webhooks").promised,
    depth: "native",
    detail: "Push delivery of every click to your own endpoint — on the roadmap.",
    href: "developers",
  },
];

export const STATUS_META: Record<Status, { label: string; color: string }> = {
  live: { label: "Live", color: "var(--color-data-green)" },
  roadmap: { label: "Roadmap", color: "var(--color-data-amber)" },
};

/** The pill text: dated roadmap entries show their date instead of the word. */
export const statusLabel = (i: Integration): string =>
  i.status === "roadmap" && i.statusDate ? i.statusDate : STATUS_META[i.status].label;

/** "native" is the TARGET depth while an entry isn't live yet — say so
 *  (brief §8.3: never show a native claim the product can't back today). */
export const depthLabel = (i: Integration): string =>
  i.depth === "native" && i.status !== "live" ? "Native · Target" : i.depth;

export const integrationHref = (i: Integration): string | undefined =>
  i.href ? routePath(i.href) : undefined;

/** Derived counts for the hero footnote — never hardcode these in prose. */
export const TOTAL_COUNT = CATALOG.length;
export const LIVE_COUNT = CATALOG.filter((i) => i.status === "live").length;
